import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import UpdateIcon from "@mui/icons-material/Update";
import themestyle from "../theme";
import { FormLabel } from "../utils/formField";
import AppDatePicker from "./AppDatePicker";

export interface BulkDateSelectorProps {
  onGenerate: (dates: string[]) => void;
  initialFromDate?: string;
  initialToDate?: string;
  isViewMode?: boolean;
}

const parseLocalDate = (str: string) => {
  if (!str) return new Date();
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatLocalDate = (date: Date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const DAYS = [
  { label: "M", value: 1 },
  { label: "T", value: 2 },
  { label: "W", value: 3 },
  { label: "T", value: 4 },
  { label: "F", value: 5 },
  { label: "S", value: 6 },
  { label: "S", value: 0 },
];

const BulkDateSelector: React.FC<BulkDateSelectorProps> = ({
  onGenerate,
  initialFromDate = "",
  initialToDate = "",
  isViewMode = false,
}) => {
  const [fromDate, setFromDate] = useState(initialFromDate);
  const [toDate, setToDate] = useState(initialToDate);
  const [activeDays, setActiveDays] = useState<number[]>([]);
  const [error, setError] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const todayStr = new Date().toISOString().split("T")[0];

  const clearError = () => setError("");

  const toggleDay = (dayValue: number) => {
    clearError();
    setActiveDays((prev) =>
      prev.includes(dayValue)
        ? prev.filter((d) => d !== dayValue)
        : [...prev, dayValue]
    );
  };

  const handleDateChange = (type: "from" | "to", value: string) => {
    clearError();
    let nextFrom = fromDate;
    let nextTo = toDate;

    if (type === "from") {
      nextFrom = value;
      setFromDate(value);
    } else {
      nextTo = value;
      setToDate(value);
    }

    if (nextFrom && nextTo && nextFrom === nextTo) {
      const day = parseLocalDate(nextFrom).getDay();
      setActiveDays((prev) => Array.from(new Set([...prev, day])));
    }
  };

  const handleGenerate = () => {
    if (!fromDate || !toDate || activeDays.length === 0) return;

    const start = parseLocalDate(fromDate);
    const end = parseLocalDate(toDate);
    const generatedDates: string[] = [];

    let current = new Date(start);
    while (current <= end) {
      if (activeDays.includes(current.getDay())) {
        generatedDates.push(formatLocalDate(current));
      }
      current.setDate(current.getDate() + 1);
    }

    if (generatedDates.length === 0) {
      setError("Selected days are not within the from and to date range.");
      return;
    }
    onGenerate(generatedDates);
    clearError();
  };

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        p: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: { xs: 2, md: 3 },
        mb: 3,
        position: "relative"
      }}
    >
      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1" }, minWidth: "150px" }}>
        <FormLabel>From Date</FormLabel>
        <AppDatePicker
          value={fromDate}
          onChange={(v) => handleDateChange("from", v)}
          minDate={todayStr}
          disabled={isViewMode}
        />
      </Box>

      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 45%", md: "1" }, minWidth: "150px" }}>
        <FormLabel>To Date</FormLabel>
        <AppDatePicker
          value={toDate}
          onChange={(v) => handleDateChange("to", v)}
          minDate={fromDate || todayStr}
          disabled={isViewMode}
        />
      </Box>

      <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 100%", md: "1" }, minWidth: "200px" }}>
        <FormLabel>Active Days</FormLabel>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            border: `1px solid ${error ? "#d32f2f" : "#E5E7EB"}`,
            borderRadius: "8px",
            height: "44px",
            px: 2,
          }}
        >
          {DAYS.map((day) => (
            <Box
              key={day.value}
              onClick={() => !isViewMode && toggleDay(day.value)}
              sx={{
                width: 28,
                height: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                cursor: isViewMode ? "default" : "pointer",
                fontSize: "12px",
                fontWeight: 600,
                transition: "all 0.2s",
                bgcolor: activeDays.includes(day.value)
                  ? themestyle.colors.activeGreen
                  : "transparent",
                color: activeDays.includes(day.value) ? "#fff" : "#6B7280",
                "&:hover": {
                  bgcolor: isViewMode 
                    ? (activeDays.includes(day.value) ? themestyle.colors.activeGreen : "transparent")
                    : (activeDays.includes(day.value)
                      ? themestyle.colors.buttonGreenHover
                      : "#F3F4F6"),
                },
              }}
            >
              {day.label}
            </Box>
          ))}
        </Box>
        {error && (
          <Typography
            sx={{
              color: "#d32f2f",
              fontSize: "12px",
              mt: 0.5,
              ml: 1,
              fontFamily: themestyle.fontFamily.Roboto
            }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Button
        variant="contained"
        onClick={handleGenerate}
        disabled={isViewMode || !fromDate || !toDate || activeDays.length === 0}
        startIcon={<UpdateIcon />}
        sx={{
          mt: { xs: 0, sm: 3, md: 3.5 },
          flex: { xs: "1 1 100%", md: "0 0 auto" },
          height: "44px",
          bgcolor: themestyle.colors.activeGreen,
          color: "#fff",
          fontWeight: 600,
          textTransform: "none",
          borderRadius: "8px",
          px: 4,
          whiteSpace: "nowrap",
          "&:hover": {
            bgcolor: themestyle.colors.buttonGreenHover,
          },
          "&.Mui-disabled": {
            bgcolor: "#E5E7EB",
            color: "#9CA3AF",
          },
        }}
      >
        Generate Schedule
      </Button>
    </Box>
  );
};
export default BulkDateSelector;